import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import AuthCallback from './pages/AuthCallback';
import ExternalAuthCallback from './pages/ExternalAuthCallback';
import Landing from './pages/Landing';
import PortalShell from './shell/PortalShell';
import Overview from './pages/Overview';
import ModuleHost from './pages/ModuleHost';
import AdminShell, { AdminOnly } from './admin/AdminShell';
import AdminClients from './admin/AdminClients';
import AdminClientDetail from './admin/AdminClientDetail';
import AdminInvites from './admin/AdminInvites';
import AdminAudit from './admin/AdminAudit';
import AdminUsers from './admin/AdminUsers';
import AdminModules from './admin/AdminModules';
import AdminModuleDataMapping from './admin/AdminModuleDataMapping';
import AdminClientverseHistory, { AdminClientverseHistoryDetail } from './admin/AdminClientverseHistory';
import AdminClientverseCoreBrowser from './admin/AdminClientverseCoreBrowser';
import AdminSystemToClientverse, { AdminSystemToClientverseQuestions, AdminSystemToClientverseStep } from './admin/AdminSystemToClientverse';
import AdminSystemLoadOrder, { AdminSystemLoadOrderSystem } from './admin/AdminSystemLoadOrder';
import AdminStagingCanonicalBrowser from './admin/AdminStagingCanonicalBrowser';
import AdminModuleAsanaBoard from './admin/AdminModuleAsanaBoard';
import AdminModuleUiSignoff from './admin/AdminModuleUiSignoff';
import AdminModuleUat from './admin/AdminModuleUat';
import AdminModulePrReview from './admin/AdminModulePrReview';
import AdminReports, { AdminReportsIndex } from './admin/AdminReports';
import AdminReportsSystems from './admin/AdminReportsSystems';

function Protected({ children }: { children: React.ReactNode }) {
  const { source, ready } = useAuth();
  if (!ready) return <div style={{ padding: 32 }}>Loading…</div>;
  if (!source) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login/:slug" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/external-callback" element={<ExternalAuthCallback />} />
          <Route path="/" element={<Protected><Landing /></Protected>} />
          <Route path="/admin" element={<Protected><AdminShell /></Protected>}>
            <Route index element={<Navigate to="clients" replace />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="clients/:clientId" element={<AdminClientDetail />} />
            <Route path="invites" element={<AdminOnly><AdminInvites /></AdminOnly>} />
            <Route path="audit" element={<AdminOnly><AdminAudit /></AdminOnly>} />
            <Route path="users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
            <Route path="modules" element={<AdminOnly><AdminModules /></AdminOnly>} />
            <Route path="modules/data-mapping" element={<AdminOnly><AdminModuleDataMapping /></AdminOnly>} />
            <Route path="modules/data-mapping/clientverse/history" element={<AdminOnly><AdminClientverseHistory /></AdminOnly>} />
            <Route path="modules/data-mapping/clientverse/history/:versionId" element={<AdminOnly><AdminClientverseHistoryDetail /></AdminOnly>} />
            <Route path="modules/data-mapping/clientverse/core/browse" element={<AdminOnly><AdminClientverseCoreBrowser /></AdminOnly>} />
            <Route path="modules/data-mapping/system-to-clientverse" element={<AdminOnly><AdminSystemToClientverse /></AdminOnly>} />
            <Route path="modules/data-mapping/system-to-clientverse/open-questions" element={<AdminOnly><AdminSystemToClientverseQuestions /></AdminOnly>} />
            <Route path="modules/data-mapping/system-to-clientverse/:slug" element={<AdminOnly><AdminSystemToClientverseStep /></AdminOnly>} />
            <Route path="modules/data-mapping/system-load-order" element={<AdminOnly><AdminSystemLoadOrder /></AdminOnly>} />
            <Route path="modules/data-mapping/system-load-order/:slug" element={<AdminOnly><AdminSystemLoadOrderSystem /></AdminOnly>} />
            <Route path="modules/data-mapping/staging/:version/browse" element={<AdminOnly><AdminStagingCanonicalBrowser /></AdminOnly>} />
            <Route path="clients/:clientId/asana-board" element={<AdminOnly><AdminModuleAsanaBoard /></AdminOnly>} />
            <Route path="clients/:clientId/staging/browse" element={<AdminOnly><AdminStagingCanonicalBrowser /></AdminOnly>} />
            <Route path="clients/:clientId/ui-signoff" element={<AdminOnly><AdminModuleUiSignoff /></AdminOnly>} />
            {/* Not AdminOnly: a client admin works the plans of the clients they are assigned to. The API
                enforces that assignment (requireAdminOrClientAdmin), and the permanent deletes on this page
                are administrators-only in the page and in the API. */}
            <Route path="clients/:clientId/uat" element={<AdminModuleUat />} />
            <Route path="clients/:clientId/pr-review" element={<AdminModulePrReview />} />
            <Route path="reports" element={<AdminOnly><AdminReports /></AdminOnly>}>
              <Route index element={<AdminReportsIndex />} />
              <Route path="systems" element={<AdminReportsSystems />} />
            </Route>
          </Route>
          <Route path="/:clientSlug" element={<Protected><PortalShell /></Protected>}>
            <Route index element={<Overview />} />
            <Route path=":moduleCode" element={<ModuleHost />} />
            <Route path=":moduleCode/:subPath" element={<ModuleHost />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
